import { useState, useCallback, useRef } from 'react';
import { authFetch } from '../services/auth';
import PCMAudioPlayer from '../utils/PCMAudioPlayer';
import {
  interviewChatWithTTS,
  type InterviewChatTTSParams,
  type InterviewConfig,
} from '../services/interviewChatService';
import {
  salaryChatWithTTS,
  type SalaryChatTTSParams,
  type SalaryNegotiationConfig,
} from '../services/salaryChatService';
import type { Message } from '../types';
import type { SalaryMessage } from '../types/salaryNegotiation';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://mmbang.site';

interface UseTTSSpeechSynthesisReturn {
  speak: (
    text: string,
    voice?: string,
    rate?: number,
    onStart?: () => void,
    onEnd?: () => void,
  ) => Promise<void>;
  // 面试场景语音合成 - 整合 ASR + Chat + TTS（仅用于后续对话）
  speakInterview: (params: {
    messages: Message[];
    interviewConfig: InterviewConfig;
    voice?: string;
    audio: string;
    onStart?: () => void;
    onEnd?: () => void;
    onComplete?: (result: {
      userText: string;
      interviewerText: string;
    }) => void;
  }) => Promise<void>;
  // 谈薪场景语音合成 - 整合 ASR + Chat + TTS
  speakSalary: (params: {
    dialogueHistory: SalaryMessage[];
    salaryConfig: SalaryNegotiationConfig;
    voice?: string;
    audio: string;
    onStart?: () => void;
    onEnd?: () => void;
    onComplete?: (result: {
      userText: string;
      hrText: string;
      isFinal: boolean;
      offerAmount?: number;
    }) => void;
  }) => Promise<void>;
  stopSpeaking: () => void;
  isSpeaking: boolean;
  error: string | null;
}

export function useSpeechSynthesis(): UseTTSSpeechSynthesisReturn {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pcmPlayerRef = useRef<PCMAudioPlayer | null>(null);

  // 停止所有播放和资源
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (pcmPlayerRef.current) {
      pcmPlayerRef.current.stop();
      pcmPlayerRef.current = null;
    }

    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    setIsSpeaking(false);
  }, []);

  // 流式播放 TTS 音频 - PCM 数据边接收边播放
  const speak = useCallback(
    async (
      text: string,
      voice?: string,
      rate?: number,
      onStart?: () => void,
      onEnd?: () => void,
    ): Promise<void> => {
      cleanup();

      try {
        setError(null);

        abortControllerRef.current = new AbortController();

        const response = await authFetch(`${API_BASE_URL}/api/tts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text,
            voice: voice || 'Cherry',
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`TTS请求失败: ${response.status}`);
        }

        if (!response.body) {
          throw new Error('没有返回响应流');
        }

        // 创建 PCM 音频播放器
        // 阿里 TTS 返回 PCM 格式：24000Hz, 16-bit signed, 单声道
        pcmPlayerRef.current = new PCMAudioPlayer(24000);
        pcmPlayerRef.current.connect();

        // 立即标记为开始播放
        setIsSpeaking(true);
        onStart?.();

        // 标记是否所有音频数据已接收完毕
        let isStreamEnded = false;
        let hasTriggeredEnd = false;

        // 设置播放结束回调
        pcmPlayerRef.current.onEnded(() => {
          if (isStreamEnded && !hasTriggeredEnd) {
            hasTriggeredEnd = true;
            setIsSpeaking(false);
            onEnd?.();
          }
        });

        // 读取 SSE 流
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let sseBuffer = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            isStreamEnded = true;
            // 检查音频是否也已经播放完毕
            if (
              pcmPlayerRef.current &&
              !pcmPlayerRef.current.isPlaying() &&
              !hasTriggeredEnd
            ) {
              hasTriggeredEnd = true;
              setIsSpeaking(false);
              onEnd?.();
            }
            break;
          }

          if (abortControllerRef.current?.signal.aborted) {
            break;
          }

          sseBuffer += decoder.decode(value, { stream: true });

          const events = sseBuffer.split('\n\n');
          sseBuffer = events.pop() || '';

          for (const event of events) {
            if (!event.trim() || event.startsWith(':')) continue;

            const lines = event.split('\n').filter((line) => line.trim());
            for (const line of lines) {
              if (!line.startsWith('data:')) continue;

              try {
                const jsonStr = line.slice(5).trim();

                if (jsonStr === '[DONE]') {
                  isStreamEnded = true;
                  continue;
                }

                const sseData = JSON.parse(jsonStr);

                // 检测 finish_reason: "stop" 表示输出完成
                const finishReason =
                  sseData.output?.finish_reason || sseData.finish_reason;
                if (finishReason === 'stop') {
                  isStreamEnded = true;
                  continue;
                }

                let base64Audio = '';

                // 适配阿里 TTS 返回结构
                if (sseData.payload?.audio) {
                  base64Audio = sseData.payload.audio;
                } else if (sseData.output?.audio?.data) {
                  base64Audio = sseData.output.audio.data;
                } else if (sseData.delta) {
                  base64Audio = sseData.delta;
                }

                if (base64Audio) {
                  // 解码 Base64 得到 PCM 数据
                  const pcmData = Uint8Array.from(atob(base64Audio), (c) =>
                    c.charCodeAt(0),
                  );

                  // 使用 PCMAudioPlayer 播放
                  pcmPlayerRef.current?.pushPCM(pcmData.buffer);
                }
              } catch (parseError) {
                // 解析失败，忽略该行
              }
            }
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        setError('合成失败，使用备用方案');
        setIsSpeaking(false);
        onEnd?.();

        return fallbackSpeak(text, rate, onStart, onEnd);
      }
    },
    [cleanup],
  );

  // 降级方案
  const fallbackSpeak = (
    text: string,
    rate?: number,
    onStart?: () => void,
    onEnd?: () => void,
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!window.speechSynthesis) {
        reject(new Error('不支持语音合成'));
        return;
      }

      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'zh-CN';
      utterance.rate = rate || 1;

      utterance.onstart = () => {
        setIsSpeaking(true);
        onStart?.();
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        onEnd?.();
        resolve();
      };

      utterance.onerror = () => {
        setIsSpeaking(false);
        onEnd?.();
        reject(new Error('播放失败'));
      };

      window.speechSynthesis.speak(utterance);
    });
  };

  // 面试场景语音合成 - 整合 ASR + Chat + TTS（仅用于后续对话）
  const speakInterview = useCallback(
    async (params: {
      messages: Message[];
      interviewConfig: InterviewConfig;
      voice?: string;
      audio: string;
      onStart?: () => void;
      onEnd?: () => void;
      onComplete?: (result: {
        userText: string;
        interviewerText: string;
      }) => void;
    }): Promise<void> => {
      cleanup();

      const {
        messages,
        interviewConfig,
        voice = 'Cherry',
        audio,
        onStart,
        onEnd,
        onComplete,
      } = params;

      try {
        setError(null);

        // 创建 PCM 音频播放器
        pcmPlayerRef.current = new PCMAudioPlayer(24000);
        pcmPlayerRef.current.connect();

        // 标记是否所有音频数据已接收完毕
        let isStreamEnded = false;
        let hasTriggeredEnd = false;
        let hasAudioStarted = false;

        // 设置播放结束回调
        pcmPlayerRef.current.onEnded(() => {
          if (isStreamEnded && !hasTriggeredEnd) {
            hasTriggeredEnd = true;
            setIsSpeaking(false);
            onEnd?.();
          }
        });

        // 调用整合接口
        await interviewChatWithTTS({
          messages,
          interviewConfig,
          voice,
          audio,
          onAudioData: (base64Audio: string) => {
            // 第一次接收到音频数据时，标记为开始播放
            if (!hasAudioStarted) {
              hasAudioStarted = true;
              setIsSpeaking(true);
              onStart?.();
            }
            // 解码 Base64 得到 PCM 数据并播放
            const pcmData = Uint8Array.from(atob(base64Audio), (c) =>
              c.charCodeAt(0),
            );
            pcmPlayerRef.current?.pushPCM(pcmData.buffer);
          },
          onComplete: (result) => {
            isStreamEnded = true;
            onComplete?.(result);
            // 检查音频是否也已经播放完毕
            if (
              pcmPlayerRef.current &&
              !pcmPlayerRef.current.isPlaying() &&
              !hasTriggeredEnd
            ) {
              hasTriggeredEnd = true;
              setIsSpeaking(false);
              onEnd?.();
            }
          },
          onError: (err) => {
            setError(err.message);
            setIsSpeaking(false);
            onEnd?.();
          },
        });
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        setError('面试语音合成失败');
        setIsSpeaking(false);
        onEnd?.();
      }
    },
    [cleanup],
  );

  // 谈薪场景语音合成 - 整合 ASR + Chat + TTS
  const speakSalary = useCallback(
    async (params: {
      dialogueHistory: SalaryMessage[];
      salaryConfig: SalaryNegotiationConfig;
      voice?: string;
      audio: string;
      onStart?: () => void;
      onEnd?: () => void;
      onComplete?: (result: {
        userText: string;
        hrText: string;
        isFinal: boolean;
        offerAmount?: number;
      }) => void;
    }): Promise<void> => {
      cleanup();

      const {
        dialogueHistory,
        salaryConfig,
        voice = 'Maia',
        audio,
        onStart,
        onEnd,
        onComplete,
      } = params;

      try {
        setError(null);

        // 创建 PCM 音频播放器
        pcmPlayerRef.current = new PCMAudioPlayer(24000);
        pcmPlayerRef.current.connect();

        // 标记是否所有音频数据已接收完毕
        let isStreamEnded = false;
        let hasTriggeredEnd = false;
        let hasAudioStarted = false;

        // 设置播放结束回调
        pcmPlayerRef.current.onEnded(() => {
          if (isStreamEnded && !hasTriggeredEnd) {
            hasTriggeredEnd = true;
            setIsSpeaking(false);
            onEnd?.();
          }
        });

        // 调用整合接口
        await salaryChatWithTTS({
          dialogueHistory,
          salaryConfig,
          voice,
          audio,
          onAudioData: (base64Audio: string) => {
            // 第一次接收到音频数据时，标记为开始播放
            if (!hasAudioStarted) {
              hasAudioStarted = true;
              setIsSpeaking(true);
              onStart?.();
            }
            // 解码 Base64 得到 PCM 数据并播放
            const pcmData = Uint8Array.from(atob(base64Audio), (c) =>
              c.charCodeAt(0),
            );
            pcmPlayerRef.current?.pushPCM(pcmData.buffer);
          },
          onComplete: (result) => {
            isStreamEnded = true;
            onComplete?.(result);
            // 检查音频是否也已经播放完毕
            if (
              pcmPlayerRef.current &&
              !pcmPlayerRef.current.isPlaying() &&
              !hasTriggeredEnd
            ) {
              hasTriggeredEnd = true;
              setIsSpeaking(false);
              onEnd?.();
            }
          },
          onError: (err) => {
            setError(err.message);
            setIsSpeaking(false);
            onEnd?.();
          },
        });
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        setError('谈薪语音合成失败');
        setIsSpeaking(false);
        onEnd?.();
      }
    },
    [cleanup],
  );

  const stopSpeaking = useCallback(() => {
    cleanup();
  }, [cleanup]);

  return {
    speak,
    speakInterview,
    speakSalary,
    stopSpeaking,
    isSpeaking,
    error,
  };
}
